/**
 * Rubriques du compte courant et raccourci de la barre du bas. Le raccourci
 * vit sur le serveur (`users.nav_shortcuts`, servi par `/auth/me`) : la même
 * barre sur tous les appareils ; le profil en cache ne sert qu'à l'afficher
 * hors ligne.
 */
import { useEffect, useMemo, useState } from 'react';
import { api, ApiError } from '../core/api.js';
import { useAuth, useCan, useTerrainView } from '../core/auth.js';
import { useOpenConflicts } from '../features/sync/use-open-conflicts.js';
import { useMessagesUnread } from '../features/messages/messages-data.js';
import {
  areaSections, areasFor, bottomBar, SHORTCUT_DEFAULT, type Area, type AreaSection, type BottomBar,
} from './areas.js';

export interface AreaNavigation {
  terrain: boolean;
  areas: Area[];
  sections: AreaSection[];
}

export function useAreas(): AreaNavigation {
  const { me } = useAuth();
  const can = useCan();
  const terrain = useTerrainView();
  const { total: conflicts } = useOpenConflicts();
  const modules = me?.enabled_modules;
  // Pastille de la rubrique Messages : seulement avec son droit (rubrique coupée : rien n'est lu).
  const unread = useMessagesUnread(can('messaging.use'))?.total;
  return useMemo(() => {
    const areas = areasFor({
      terrain,
      access: { can, modules: modules ?? [] },
      // Tant que la liste n'est pas lue, l'entrée reste : elle ne disparaît que vide.
      conflictsPending: conflicts === undefined || conflicts > 0,
      unreadMessages: unread,
    });
    return { terrain, areas, sections: areaSections(areas, terrain) };
  }, [terrain, can, modules, conflicts, unread]);
}

/** Message lisible d'un enregistrement refusé ou impossible. */
function saveError(failure: unknown): string {
  if (failure instanceof ApiError && failure.code === 'network_error') {
    return 'Hors ligne : raccourci non enregistré. Réessayez une fois connecté.';
  }
  return failure instanceof Error ? failure.message : 'Raccourci non enregistré.';
}

export interface BottomBarState extends BottomBar {
  choose: (path: string) => void;
  saving: boolean;
  error: string | undefined;
}

/** Ancien stockage par appareil (avant le 15/10/2026) : effacé, jamais repris. */
const LEGACY_SHORTCUTS_PREFIX = 'myequip.shortcuts.';

function clearLegacyShortcuts(): void {
  try {
    for (let index = localStorage.length - 1; index >= 0; index -= 1) {
      const key = localStorage.key(index);
      if (key?.startsWith(LEGACY_SHORTCUTS_PREFIX)) localStorage.removeItem(key);
    }
  } catch {
    /* stockage indisponible : rien à nettoyer */
  }
}

export function useBottomBar(nav: AreaNavigation): BottomBarState {
  const { me, applyConfirmed } = useAuth();
  const [pending, setPending] = useState<string>();
  const [error, setError] = useState<string>();
  useEffect(clearLegacyShortcuts, []);
  const fallback = nav.terrain ? SHORTCUT_DEFAULT.terrain : SHORTCUT_DEFAULT.bureau;
  const bar = bottomBar(nav.areas, pending ? [pending] : me?.nav_shortcuts, fallback);
  const choose = (path: string): void => {
    if (!bar.options.some((area) => area.to === path) || path === bar.shortcut?.to) return;
    setPending(path);
    setError(undefined);
    api<{ navShortcuts: string[] | null }>('/users/me/shortcuts', { method: 'PUT', body: { paths: [path] } })
      .then((saved) => applyConfirmed({ nav_shortcuts: saved.navShortcuts }))
      .catch((failure: unknown) => setError(saveError(failure)))
      .finally(() => setPending(undefined));
  };
  return { ...bar, choose, saving: pending !== undefined, error };
}
